import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useDispatch } from 'react-redux';

// Shared UI Library Components
import { CustomIcon, CustomTooltip, EIconName, SwitchElement } from '@ppmcore/seven-ppm-core-shared-components-react';

// Styles
import './free-services.scss';

// Components
import {
  DeleteCostPerSessionModal
} from '../../../../modals/delete-cost-per-session-modal/delete-cost-per-session-modal';

// Store entities
import {
  updateFreeMinutes, updatePricePerMessage,
  updatePricePerMinute
} from '../../../../store/current-counsellor/current-counsellor.thunks';
import { openNotification } from '../../../../store/app-notifications/app-notifications.thunks';

// Models
import { ICounsellor, IFreeMinutes } from '../../../../interfaces/counsellors.interfaces';
import { TPPMType } from '../../../../interfaces/price-settings.interfaces';

export type TFreeServicesRef = {
  resetState: () => void;
};

type TFreeServicesProps = {
  counsellor: ICounsellor;
  free_minutes: IFreeMinutes;
  is_stripe_connected: boolean;
};

export const FreeServices = forwardRef<TFreeServicesRef, TFreeServicesProps>((
  { counsellor, free_minutes, is_stripe_connected }: TFreeServicesProps, ref
) => {

  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [deleteCorrespondenceOpen, setDeleteCorrespondenceOpen] = useState<boolean>(false);

  const dispatch = useDispatch<any>();

  const isSwitchesDisabled = !free_minutes.full_free_service_enabled;

  const toggleRates = (type: TPPMType) => {
    dispatch(updatePricePerMinute({
      worker_id: counsellor.id,
      ...(type === 'call_rate' && {
        is_call_rate_enabled: Number(!counsellor.is_video_call_enabled),
        is_audio_call_rate_enabled: Number(counsellor.is_audio_call_enabled),
        is_conversation_rate_enabled: Number(counsellor.is_conversation_enabled),
      }),
      ...(type === 'audio_call_rate' && {
        is_call_rate_enabled: Number(counsellor.is_video_call_enabled),
        is_audio_call_rate_enabled: Number(!counsellor.is_audio_call_enabled),
        is_conversation_rate_enabled: Number(counsellor.is_conversation_enabled),
      }),
      ...(type === 'conversation_rate' && {
        is_call_rate_enabled: Number(counsellor.is_video_call_enabled),
        is_audio_call_rate_enabled: Number(counsellor.is_audio_call_enabled),
        is_conversation_rate_enabled: Number(!counsellor.is_conversation_enabled),
      }),
    }));
  };

  const toggleEditMode = (type: TPPMType, checked: boolean, event: any) => {
    if (!checked && counsellor.rates_enabled_count === 1) {
      setDeleteOpen(true);
      event.preventDefault();
      return;
    }

    toggleRates(type);
  };

  const lastRatesDeleteHandler = (state: boolean): void => {
    setDeleteOpen(false);
    if (!state) return;

    const ppmRates = {
      audio_call_rate: counsellor.is_audio_call_enabled,
      call_rate: counsellor.is_video_call_enabled,
      conversation_rate: counsellor.is_conversation_enabled,
    }
    const lastType = Object.keys(ppmRates).find((key) => ppmRates[key as keyof typeof ppmRates]);

    if (!lastType) return;

    toggleRates(lastType as TPPMType);
  }

  const toggleFullFreeServicesHandler = (full_free_service_enabled: boolean, event: any): void => {
    if (!is_stripe_connected && !full_free_service_enabled) {
      event.preventDefault();
      dispatch(openNotification({
        type: 'error',
        description: 'Please connect your Stripe account first to deactivate the free services'
      }));
      return;
    }

    dispatch(updateFreeMinutes({
      id: counsellor.id,
      free_minutes: {
        ...free_minutes,
        full_free_service_enabled
      }
    }));
  }

  // Correspondence rates
  const toggleCorrespondenceRates = (): void => {
    dispatch(updatePricePerMessage({
      worker_id: counsellor.id,
      is_message_rate_enabled: !counsellor.is_messages_enabled
    }));
  }

  const lastCorrespondenceRatesDeleteHandler = (state: boolean): void => {
    setDeleteCorrespondenceOpen(false);
    if (!state) return;
    toggleCorrespondenceRates();
  }

  const toggleCorrespondenceEditMode = (checked: boolean, event: any): void => {
    if (!checked && counsellor.rates_enabled_count === 1) {
      setDeleteCorrespondenceOpen(true);
      event.preventDefault();
      return;
    }

    toggleCorrespondenceRates();
  }

  useImperativeHandle(ref, () => ({
    resetState(): void {}
  }));

  return (
    <div className="free-services">

      <DeleteCostPerSessionModal open={ deleteOpen }
                                 isLastItem={ counsellor?.rates_enabled_count === 1 }
                                 title={ 'Are you sure you want to delete Cost per Minute?' }
                                 onClose={ lastRatesDeleteHandler }/>

      <DeleteCostPerSessionModal open={ deleteCorrespondenceOpen }
                                 isLastItem={ counsellor?.rates_enabled_count === 1 }
                                 title={ 'Are you sure you want to delete Request and Cost?' }
                                 onClose={ lastCorrespondenceRatesDeleteHandler }/>

      <div className="free-services--header">
        <div className="header-action">
          <SwitchElement checked={ free_minutes.full_free_service_enabled }
                         onChange={ toggleFullFreeServicesHandler }/>
        </div>
        <div className={ `header-title ${ !free_minutes.full_free_service_enabled ? 'disabled-block' : '' }` }>
          Provide Services for FREE <CustomTooltip title={ 'Provide services for free' }>
          <CustomIcon size={ 'size-md' } name={ EIconName.Info }/>
        </CustomTooltip>
        </div>
      </div>

      <div className="free-services--body">
        <div className="body-item">
          <CustomIcon name={ EIconName.CameraOn } size={ 'size-xll' }/>
          <span className="body-item--text">
           Video Consultation
          </span>
          <SwitchElement disabled={ isSwitchesDisabled }
                         checked={ counsellor?.is_video_call_enabled }
                         onChange={ (checked, event) => toggleEditMode('call_rate', checked, event) }/>
        </div>
        <div className="body-item">
          <CustomIcon name={ EIconName.MicOn } size={ 'size-xll' }/>
          <span className="body-item--text">
            Voice Consultation
          </span>
          <SwitchElement disabled={ isSwitchesDisabled }
                         checked={ counsellor?.is_audio_call_enabled }
                         onChange={ (checked, event) => toggleEditMode('audio_call_rate', checked, event) }/>
        </div>
        <div className="body-item">
          <CustomIcon name={ EIconName.ChatIcon } size={ 'size-xll' }/>
          <span className="body-item--text">
            Text Chat
          </span>
          <SwitchElement disabled={ isSwitchesDisabled }
                         checked={ counsellor?.is_conversation_enabled }
                         onChange={ (checked, event) => toggleEditMode('conversation_rate', checked, event) }/>
        </div>
        <div className="body-item">
          <CustomIcon name={ EIconName.Message } size={ 'size-xll' }/>
          <span className="body-item--text">
            Correspondence
          </span>
          <SwitchElement disabled={ isSwitchesDisabled }
                         checked={ counsellor?.is_messages_enabled }
                         onChange={ (checked, event) => toggleCorrespondenceEditMode(checked, event) }/>
        </div>
      </div>

      {/*<div*/ }
      {/*  className={ `price-per-minute--description` }>*/ }
      {/*  <FreeMinutesItem initialMinutes={ free_minutes.free_service_minutes }*/ }
      {/*                   initialBuffers={ free_minutes.free_buffer_minutes }*/ }
      {/*                   enableFreeMode={ free_minutes.free_minutes_enabled }*/ }
      {/*                   toggleFreeMode={ toggleFreeMinutesHandler }*/ }
      {/*                   saveChanges={ saveFreeMinutesHandler }*/ }
      {/*                   disabledBlock={ free_minutes.full_free_service_enabled }*/ }
      {/*  />*/ }
      {/*</div>*/ }
    </div>
  );
})
